"use client";

import { DamacAPI } from "@utils/damac/api";
import { motion, useMotionValueEvent, useTransform } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Script from "next/script";
import React, { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";

import { handleMapScrollDownImage } from "@/app/[locale]/my-profile/actions";
import MapPoint from "@/components/Map/MapPoint";
import OverlayContainer from "@/components/Map/OverlayContainer";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import useGoogleMap from "@/hooks/useGoogleMap";
import useMediaQuery from "@/hooks/useMediaQuery";

import "./MapContainer.css";
import { CityLocationMap, Poi, Point } from "./type";

function check(poi: Poi, center: Point, radius: number) {
  var km = radius / 1000;
  var kx = Math.cos((Math.PI * center.lat) / 180) * 111;
  var dx = Math.abs(center.lng - poi.position.lng) * kx;
  var dy = Math.abs(center.lat - poi.position.lat) * 111;
  return Math.sqrt(dx * dx + dy * dy) <= km;
}

type logoScrollDownImage = {
  title: string;
  url: string;
  width: number;
  height: number;
};

const MapContainer: FC<CityLocationMap> = ({ container, center, poi, zoom, ranges, carImage, backgroundColor, textColor }) => {
  const t = useTranslations();
  const contentRef = useRef<HTMLDivElement>(null);
  const carTrackRef = useRef<HTMLDivElement>(null);
  const [points, setPoints] = useState<Poi[]>(poi);
  const [showTooltip, setShowTooltip] = useState(false);
  const car = useRef<HTMLDivElement>(null);
  const mainCircle = useRef<google.maps.Circle>();
  const mapRef = useRef<HTMLDivElement>(null);
  const [radius, setRadius] = useState<number>(0);
  const [scrollDownImage, setScrollDownImage] = useState<logoScrollDownImage | null>(null);
  const { map } = useGoogleMap(mapRef, center, zoom);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const locale = useLocale();
  const [language, region] = locale.split("-");
  const isRTL = language === "ar";

  useEffect(() => {
    async function fetchScrollDownImage() {
      const logoScrollDownImage = await handleMapScrollDownImage("scroll-down-arrow");
      setScrollDownImage(logoScrollDownImage.logoScrollDownImage || "");
    }
    fetchScrollDownImage();
  }, []);

  const carX = useTransform(
    container,
    [0, 1],
    isRTL
      ? [
          `${car.current ? car.current.getBoundingClientRect().width / 2 : 0}px`,
          `${
            carTrackRef.current && car.current
              ? car.current.getBoundingClientRect().width * (isDesktop ? 1.5 : 0.5) - carTrackRef.current.getBoundingClientRect().width
              : 0
          }px`,
        ]
      : [
          `${car.current ? -(car.current.getBoundingClientRect().width / 2) : 0}px`,
          `${carTrackRef.current && car.current ? carTrackRef.current.getBoundingClientRect().width - car.current.getBoundingClientRect().width * (isDesktop ? 1.5 : 0.5) : 0}px`,
        ],
  );

  useMotionValueEvent(carX, "change", (latest) => {
    if (carTrackRef.current && car.current && mainCircle.current && latest) {
      const maxRange = Math.max(...ranges.map((o) => o.radius));
      const carPosition = car.current.getBoundingClientRect().x;
      const containerPosition = carTrackRef.current?.getBoundingClientRect().x;
      const containerSize = carTrackRef.current?.getBoundingClientRect().width;

      const relativeCarPosition = isRTL
        ? containerSize - (carPosition - containerPosition + car.current.offsetWidth / 2)
        : carPosition - containerPosition + car.current.offsetWidth / 2;
      const percentage = (relativeCarPosition * 100) / containerSize;
      setRadius((percentage * maxRange) / 100);
    }
  });

  const circleOpions = useMemo(() => {
    return {
      strokeColor: "#222222",
      strokeOpacity: 0.1,
      strokeWeight: 3,
      fillColor: "#222222",
      fillOpacity: 0.1,
      clickable: false,
      center: center,
    };
  }, [center]);

  useEffect(() => {
    if (map) {
      // console.info("init circle on map");
      const drawCircle = new google.maps.Circle(circleOpions);
      drawCircle?.setMap(map);
      mainCircle.current = drawCircle;
    }
  }, [map, circleOpions]);

  useEffect(() => {
    if (map && mainCircle.current && typeof radius === "number" && !isNaN(radius)) {
      try {
        mainCircle.current.setRadius(radius);
        const bounds = new google.maps.LatLngBounds();
        let hasValidBounds = false;

        const newPois = poi?.map((poi) => {
          const newPoi = { ...poi };
          newPoi.visible = check(poi, center, radius);
          if (
            newPoi?.visible &&
            newPoi?.position &&
            typeof newPoi?.position.lat === "number" &&
            typeof newPoi?.position?.lng === "number" &&
            !isNaN(newPoi.position.lat) &&
            !isNaN(newPoi.position.lng)
          ) {
            bounds.extend(newPoi.position);
            hasValidBounds = true;
          }
          return newPoi;
        });

        setPoints(newPois);

        if (radius > 1000 && hasValidBounds) {
          const circleBounds = mainCircle?.current?.getBounds();
          if (circleBounds && !circleBounds.isEmpty()) {
            map?.fitBounds(circleBounds, 0);
          } else if (!bounds.isEmpty()) {
            map?.fitBounds(bounds, 0);
          }
        }
      } catch (error) {
        console.warn("Map bounds error:", error);
      }
    }
  }, [radius, map, poi, center]);

  return (
    <div className="h-full flex flex-col">
      <div className={`grow w-full flex flex-col`} ref={contentRef}>
        <div className="flex shrink-0 items-center w-4/5 mx-auto">
          <div ref={carTrackRef} className="grow translate-y-[12px] md:translate-y-[21px] relative z-10">
            <div className="grow flex justify-between" style={{ color: textColor ?? undefined }}>
              {ranges?.map((item: any, i: number) => {
                return (
                  <Fragment key={`range-${i}`}>
                    <div className="text-center block relative map-point">
                      <div
                        className={`font-primary font-medium text-xs md:text-s leading-4 tracking-widest text-center ml-3xs mb-xxl absolute bottom-0 left-1/2 translate-x-[-50%]  ${isDesktop ? "" : "truncate-text"} `}
                        onTouchStart={item?.radius === 0 ? () => setShowTooltip(true) : undefined}
                        onTouchEnd={item?.radius === 0 ? () => setShowTooltip(false) : undefined}
                      >
                        {item.label}
                      </div>
                      {showTooltip && item?.radius === 0 && (
                        <div
                          className="tooltip "
                          style={{ left: language === "ar" ? 0 : "50%", right: language === "ar" ? "50%" : "0", fontSize: "12px" }}
                        >
                          {item.label}
                        </div>
                      )}
                      <div
                        className="mx-auto aspect-square bg-black-0 border-[7px] border-white rounded-full w-l"
                        style={{ backgroundColor: textColor ?? undefined, borderColor: backgroundColor ?? undefined }}
                      ></div>
                    </div>
                    <div
                      className={`border-b grow translate-y-[-12px] border-black-0 ${i === ranges.length - 1 ? "max-w-[80px] hidden md:block" : ""}`}
                    />
                  </Fragment>
                );
              })}
            </div>
            <motion.div
              ref={car}
              transition={{ ease: "easeOut", duration: 0.5 }}
              className="absolute top-0 md:top-[-50%] origin-left"
              style={{ x: carX }}
            >
              <Image
                className={`pointer-events-none w-3xl md:w-4xl ${isRTL ? "rotate-180" : ""}`}
                src={carImage.url}
                alt={t("citylocation.discoverArea")}
                width={110}
                height={50}
              />
            </motion.div>
          </div>

          {/* To hide the Discover Area button for desktop views, 
          comment out the following code. Uncomment it when needed it back. */}

          {/* <Button
            size={ButtonSize.MEDIUM}
            customClass="!hidden md:!block mx-auto translate-y-l relative z-10"
            theme={ButtonTheme.BLACK}
            type="button"
            variant={ButtonVariant.PRIMARY}
            href={`http://maps.google.com/maps?q=${center.lat},${center.lng}`}
            target="_blank"
          >
            {t("citylocation.discoverArea")}
          </Button> */}
          {scrollDownImage && (
            <div className="flex flex-col items-center mt-[20px] absolute bottom-0 left-1/2 -translate-x-1/2 z-[9999]">
              <Image src={scrollDownImage?.url} alt={scrollDownImage?.title} width={177} height={90} />
            </div>
          )}
        </div>
        <div className="grow" style={{ color: "#222222" }}>
          <div className="h-full disable-interaction" id="map" ref={mapRef}>
            {map && (
              <OverlayContainer map={map} position={center} key={`poi-center`}>
                <MapPoint index={0} visible={true} text={ranges[0].label} className="main-info-window" />
              </OverlayContainer>
            )}
            {map &&
              points?.map((poi, index) => {
                return (
                  <OverlayContainer map={map} position={poi.position} key={`poi-${index}`}>
                    <MapPoint index={index} visible={poi?.visible ?? true} text={poi.title} icon={poi.icon} />
                  </OverlayContainer>
                );
              })}
          </div>

          {/* To hide the Discover Area button for mobile views, 
          comment out the following code. Uncomment it when needed it back. */}

          {/* <Button
            size={ButtonSize.MEDIUM}
            customClass="!block md:!hidden mx-auto translate-y-[-122px] flex items-center justify-center w-fit"
            theme={ButtonTheme.BLACK}
            type="button"
            variant={ButtonVariant.PRIMARY}
            href={`http://maps.google.com/maps?q=${center.lat},${center.lng}`}
            target="_blank"
          >
            {t("citylocation.discoverArea")}
          </Button> */}
        </div>
      </div>
    </div>
  );
};

export default MapContainer;
